import { useState } from 'react';
import { FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { OrderDetail } from '@/api/orders';
import { useAuth } from '@/auth/useAuth';
import { BalanceReport } from '../BalanceReport';
import { fmtDate, money } from '../lib';
import { InvoiceSourceChips } from './sourceChips';

/**
 * The captured invoices and the selected receiving's computed balance. The
 * balance IS money — GET /orders/balance is orders:read:costs on the server —
 * so without that read the tab lists the invoices and receivings as facts
 * (numbers, dates, balanced or not) and fetches no report.
 */
export function ReceivingTab({ detail }: { detail: OrderDetail }) {
  const showCosts = useAuth().can('orders:read:costs');
  const [openId, setOpenId] = useState<string | null>(null);
  const current = openId ?? detail.receipts[0]?.id ?? null;
  return (
    <section className="receiving-workspace">
      {detail.invoices.length > 0 && (
        <div className="mb-5 grid gap-3 border-y border-border py-4">
          {detail.invoices.map((inv) => (
            <div key={inv.id} className="flex flex-wrap items-center gap-3">
              <FileText size={17} className="text-muted-foreground" />
              <span className="font-medium">{inv.invoiceNo || 'Invoice'}</span>
              <span className="text-xs text-muted-foreground">{fmtDate(inv.invoiceDate)}</span>
              {showCosts && <span className="ml-auto tabular-nums">{money(inv.total, detail.currency)}</span>}
              <InvoiceSourceChips inv={inv} journeyId={detail.id} />
            </div>
          ))}
        </div>
      )}
      {detail.receipts.length === 0 || current == null ? (
        <p className="flex flex-col items-center gap-3 px-6 py-12 text-center text-[14px] text-muted-foreground">No receiving recorded yet.</p>
      ) : (
        <>
          <div className="mb-5 flex flex-wrap items-center gap-2" aria-label="Select receiving">
            {detail.receipts.map((receipt) => (
              <Button
                key={receipt.id}
                variant={current === receipt.id ? 'secondary' : 'ghost'}
                aria-pressed={current === receipt.id}
                onClick={() => setOpenId(receipt.id)}
              >
                #{receipt.receivingId}{receipt.balancedAt ? ' · Balanced' : ''}
              </Button>
            ))}
          </div>
          {showCosts && <BalanceReport key={current} receiptId={current} currency={detail.currency} />}
        </>
      )}
    </section>
  );
}
